import { useNavigate } from 'react-router-dom';
import { Button, ModalOverlay } from './ui';

export type EarlyAccessKind = 'internships' | 'mentors';

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const COOKIES: Record<EarlyAccessKind, string> = {
  internships: 'pe_early_intern_prompt',
  mentors: 'pe_early_mentor_prompt',
};

const COPY: Record<EarlyAccessKind, { title: string; look: string; dialogId: string }> = {
  internships: {
    title: 'Clinic booking opens 20 October 2026',
    look: 'You can still look at the clinics. From 20 October, people with a plan book first — you then have 14 days to book your first week, and that is when your plan time starts.',
    dialogId: 'early-intern',
  },
  mentors: {
    title: 'Mentor questions open 20 October 2026',
    look: 'You can still look at who is here. From 20 October, people with a plan can ask. Questions do not start your plan length — booking your first clinic week does.',
    dialogId: 'early-mentor',
  },
};

function promptCookieSeen(kind: EarlyAccessKind): boolean {
  const name = COOKIES[kind];
  try {
    return document.cookie.split(';').some(part => part.trim().startsWith(`${name}=`));
  } catch {
    return false;
  }
}

function markPromptSeen(kind: EarlyAccessKind) {
  const name = COOKIES[kind];
  try {
    document.cookie = `${name}=1; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

export function earlyAccessPromptShouldOpen(kind: EarlyAccessKind) {
  return !promptCookieSeen(kind);
}

export function EarlyAccessInternModal({
  kind = 'internships',
  onClose,
}: {
  kind?: EarlyAccessKind;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const text = COPY[kind];

  const close = () => {
    markPromptSeen(kind);
    onClose();
  };

  return (
    <ModalOverlay>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${text.dialogId}-title`}
        aria-describedby={`${text.dialogId}-body`}
        className="app-dialog bg-surface shadow-pop w-full max-w-md p-6 sm:p-8"
      >
        <h2 id={`${text.dialogId}-title`} className="font-heading text-xl font-bold text-ink mb-3">
          {text.title}
        </h2>
        <p id={`${text.dialogId}-body`} className="text-sm text-ink-soft mb-4">
          {text.look}
        </p>
        <p className="text-sm font-semibold text-sand-strong mb-6">Limited 30% off applied.</p>
        <div className="flex flex-col gap-2">
          <Button
            className="w-full"
            size="lg"
            onClick={() => {
              close();
              navigate('/plans');
            }}
          >
            Browse all plans
          </Button>
          <Button className="w-full" variant="ghost" onClick={close}>
            I’ll check later
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
